// src/pages/services/ITservices/AppDevelopment/AppDevelopment.js

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./AppDevelopment.css";
import { companyInfo } from "../../../../CompanyInfo/CompanyInfo";
import {
  Smartphone, Cpu, Layers, Search, CheckCircle2, ArrowRight,
  ChevronDown, Play, Send, Phone, ShieldCheck, Terminal,
  Cloud, Database, Star, Rocket, Code2, Server, Laptop
} from "lucide-react";

// ─── DATA DEFINITIONS ────────────────────────────────────────────────────────

const stats = [
  { value: "150+", label: "Apps Launched", icon: <Smartphone size={24} />, color: "#2563EB" },
  { value: "98%", label: "Client Satisfaction", icon: <ShieldCheck size={24} />, color: "#7C3AED" },
  { value: "4.9 / 5", label: "App Store Rating", icon: <Star size={24} fill="#F59E0B" stroke="#F59E0B" />, color: "#10B981" },
  { value: "3×", label: "Faster Launch Time", icon: <Rocket size={24} />, color: "#F59E0B" },
];

const innerServices = [
  {
    icon: <Smartphone size={24} />,
    title: "iOS App Development",
    desc: "Premium native iOS applications designed for iPhones and iPads. Engineered using Swift, SwiftUI, and Xcode, conforming strictly to Apple human interface guidelines.",
    color: "#2563EB",
    bg: "#EFF6FF",
    tags: ["Swift", "SwiftUI", "Apple Standard"],
  },
  {
    icon: <Smartphone size={24} />,
    title: "Android App Development",
    desc: "Robust native Android applications deployed using Kotlin and Jetpack Compose. Optimized to operate seamlessly across the entire spectrum of Android devices.",
    color: "#7C3AED",
    bg: "#F5F3FF",
    tags: ["Kotlin", "Jetpack Compose", "Material Design"],
  },
  {
    icon: <Layers size={24} />,
    title: "Cross-Platform Frameworks",
    desc: "Cost-effective, high-performance hybrid applications built using Flutter or React Native. Share a single codebase to deliver near-native experiences on both stores.",
    color: "#EC4899",
    bg: "#FDF2F8",
    tags: ["Flutter", "React Native", "Single Codebase"],
  },
  {
    icon: <Cpu size={24} />,
    title: "App Backend & APIs",
    desc: "Engineering secure backend server systems and database synchronization structures. We integrate real-time databases, push notifications, and analytics APIs.",
    color: "#10B981",
    bg: "#ECFDF5",
    tags: ["Push Notifications", "Cloud Sync", "REST APIs"],
  },
  {
    icon: <Laptop size={24} />,
    title: "UI/UX Mobile Design",
    desc: "Creating interactive mobile wireframes, user journeys, and high-fidelity clickable prototypes in Figma. Crafted for intuitive navigation and modern layouts.",
    color: "#F59E0B",
    bg: "#FFFBEB",
    tags: ["Mobile Wireframes", "Figma", "User Journey"],
  },
  {
    icon: <ShieldCheck size={24} />,
    title: "App Store Publishing & QA",
    desc: "Testing across multiple device screens. We handle the submission, compliance checks, and publishing processes for Google Play and Apple App Stores.",
    color: "#06B6D4",
    bg: "#ECFEFF",
    tags: ["Play Store", "Apple Developer", "QA Compliance"],
  },
];

const lifecycleSteps = [
  {
    num: "01",
    icon: <Search size={24} />,
    title: "Product Blueprint",
    desc: "Define user personas, chart user journeys, detail product features, and outline a clear release roadmap.",
    color: "#2563EB",
    bg: "#EFF6FF",
  },
  {
    num: "02",
    icon: <Layers size={24} />,
    title: "Interactive Prototyping",
    desc: "Designing responsive screen wireframes and interactive visuals to finalize the application's look and flow.",
    color: "#7C3AED",
    bg: "#F5F3FF",
  },
  {
    num: "03",
    icon: <Code2 size={24} />,
    title: "Sprint-Based Coding",
    desc: "Writing clean mobile code in agile development blocks. Features are delivered in incremental updates.",
    color: "#10B981",
    bg: "#ECFDF5",
  },
  {
    num: "04",
    icon: <Rocket size={24} />,
    title: "Compliance & Submission",
    desc: "Publishing your application to the official app stores, passing validation, and setting up analytics dashboards.",
    color: "#F59E0B",
    bg: "#FFFBEB",
  },
];

const techStack = [
  {
    category: "Languages & Core",
    items: [
      { name: "Swift & SwiftUI", icon: <Smartphone size={18} /> },
      { name: "Kotlin & Compose", icon: <Smartphone size={18} /> },
      { name: "Dart (Flutter)", icon: <Terminal size={18} /> },
      { name: "JavaScript / TS", icon: <Layers size={18} /> },
    ],
  },
  {
    category: "Cross-Platform Frameworks",
    items: [
      { name: "Flutter", icon: <Layers size={18} /> },
      { name: "React Native", icon: <Layers size={18} /> },
      { name: "Firebase Backend", icon: <Server size={18} /> },
      { name: "SQLite & Room", icon: <Database size={18} /> },
    ],
  },
  {
    category: "Backend & Cloud Services",
    items: [
      { name: "AWS Cloud Hosting", icon: <Cloud size={18} /> },
      { name: "Node.js & APIs", icon: <Server size={18} /> },
      { name: "Apple App Store Connect", icon: <ShieldCheck size={18} /> },
      { name: "Google Play Console", icon: <ShieldCheck size={18} /> },
    ],
  },
];

const faqs = [
  {
    q: "Do you build native apps or hybrid cross-platform apps?",
    a: "We do both. We build pure native apps using Swift (iOS) and Kotlin (Android) for projects requiring maximum device hardware utilization. For cost-effective and faster time-to-market deployments, we develop using Flutter or React Native, which share a single codebase for both stores.",
  },
  {
    q: "Will you help with publishing the app to the Google Play Store and Apple App Store?",
    a: "Yes. Our publishing and compliance team handles the entire process. We prepare the store listing assets, guide you through setting up Apple and Google developer accounts, submit the build, and address any feedback from the store reviewers.",
  },
  {
    q: "Do you provide post-launch maintenance and updates?",
    a: "Yes. We offer monthly support plans to handle OS upgrades (such as major iOS or Android updates), address bug fixes, optimize battery consumption, and release incremental feature updates to keep your application functional.",
  },
  {
    q: "How do you secure user data inside the application?",
    a: "We implement industry-standard encryption protocols. This includes securing local data caches using Keychain (iOS) and EncryptedSharedPreferences (Android), utilizing SSL/TLS pinning for network calls, and conducting API token authentication checks.",
  },
  {
    q: "Can you integrate custom payment gateways or external APIs?",
    a: "Yes. We regularly connect custom backend REST/GraphQL APIs, and integrate popular payment gateways (Apple Pay, Google Pay, Stripe, PayPal, Razorpay) as well as third-party services like Google Maps, Twilio, and social login setups.",
  },
];

// ─── COMPONENT DEFINITION ────────────────────────────────────────────────────

export default function AppDevelopment() {
  const [openFaq, setOpenFaq] = useState(null);
  const revealRefs = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("ad-shown"); }),
      { threshold: 0.1 }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const addReveal = (el) => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };

  return (
    <div className="ad-page">

      {/* ======================================================
          SECTION 1 — HERO
      ====================================================== */}
      <section className="ad-hero">
        <div className="ad-hero-bg-pattern" />
        <div className="ad-hero-bg-gradient" />

        <div className="ad-container">
          <div className="ad-hero-layout">
            {/* Hero Left Content */}
            <div className="ad-hero-content">
              <div className="ad-hero-pill">
                <span className="ad-hero-pill-dot" />
                IT Services &nbsp;·&nbsp; Mobile App Development
              </div>

              <h1>
                High-Performance <br />
                <span className="ad-highlight">Mobile Apps</span> Built for Business Growth
              </h1>

              <p className="ad-hero-desc">
                EduITalent engineers premium native iOS, Android, and cross-platform hybrid applications.
                From scoping features to launching on the official stores, we deliver secure,
                fast, and beautiful mobile products that keep your users engaged.
              </p>

              <div className="ad-hero-check-list">
                {["iOS (Swift) & Android (Kotlin) Native", "Cross-Platform (Flutter / React Native)", "App Store & Play Store Publishing Support"].map((item, idx) => (
                  <div key={idx} className="ad-hero-check-item">
                    <CheckCircle2 size={16} className="ad-check-icon-blue" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <div className="ad-hero-btns">
                <Link to="/contact" className="ad-btn-primary">
                  Start Your Project <ArrowRight size={16} />
                </Link>
                <a href="#ad-tech-stack" className="ad-btn-ghost">
                  <Play size={12} fill="currentColor" className="ad-play-icon" /> View Tech Stack
                </a>
              </div>
            </div>

            {/* Hero Right Visual (Smartphone Mockup) */}
            <div className="ad-hero-right">
              <div className="ad-phone-mock animate-float">
                <div className="ad-phone-speaker" />
                <div className="ad-phone-screen">
                  {/* Mock Mobile UI */}
                  <div className="ad-mobile-header">
                    <div className="ad-mobile-title">Dashboard</div>
                    <div className="ad-mobile-avatar">ET</div>
                  </div>
                  <div className="ad-mobile-stats">
                    <div className="ad-mstat-card blue">
                      <span>Users</span>
                      <strong>14.2K</strong>
                    </div>
                    <div className="ad-mstat-card purple">
                      <span>Revenue</span>
                      <strong>$8.4K</strong>
                    </div>
                  </div>
                  <div className="ad-mobile-list-title">Active Sprints</div>
                  <div className="ad-mobile-list">
                    <div className="ad-mobile-item">
                      <div className="ad-mitem-icon"><Code2 size={14} /></div>
                      <div className="ad-mitem-info">
                        <strong>Feature Complete</strong>
                        <span>Native Bluetooth API</span>
                      </div>
                    </div>
                    <div className="ad-mobile-item">
                      <div className="ad-mitem-icon"><ShieldCheck size={14} /></div>
                      <div className="ad-mitem-info">
                        <strong>QA Verification</strong>
                        <span>Secure Cache Crypt</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="ad-phone-home-btn" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 2 — STATS BAR
      ====================================================== */}
      <section className="ad-stats-bar">
        <div className="ad-container">
          <div className="ad-stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="ad-stats-item">
                <div className="ad-stats-icon" style={{ color: s.color, background: `${s.color}14` }}>
                  {s.icon}
                </div>
                <div>
                  <div className="ad-stats-value" style={{ color: s.color }}>{s.value}</div>
                  <div className="ad-stats-label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 3 — INNER SERVICES GRID
      ====================================================== */}
      <section className="ad-services-section">
        <div className="ad-container">
          <div className="ad-section-hdr-center ad-reveal" ref={addReveal}>
            <span className="ad-badge-lbl">App Services</span>
            <h2>Core Mobile Development Specialisations</h2>
            <p>End-to-end native development, cross-platform engineering, and publishing support built for growth.</p>
          </div>

          <div className="ad-services-grid">
            {innerServices.map((svc, i) => (
              <div className="ad-svc-card ad-reveal" key={i} ref={addReveal} style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="ad-svc-icon-box" style={{ background: svc.bg, color: svc.color }}>
                  {svc.icon}
                </div>
                <h3>{svc.title}</h3>
                <p>{svc.desc}</p>
                <div className="ad-svc-tags">
                  {svc.tags.map((tag, idx) => (
                    <span key={idx} className="ad-svc-tag" style={{ background: svc.bg, color: svc.color }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 4 — DEVELOPMENT PROCESS
      ====================================================== */}
      <section className="ad-process-section">
        <div className="ad-container">
          <div className="ad-section-hdr-center ad-reveal" ref={addReveal}>
            <span className="ad-badge-lbl">Our Process</span>
            <h2>How We Build Mobile Apps</h2>
            <p>A structured, agile development cycle ensuring compliance, speed, and clean code submissions.</p>
          </div>

          <div className="ad-process-grid">
            {lifecycleSteps.map((step, i) => (
              <div className="ad-process-card ad-reveal" key={i} ref={addReveal} style={{ transitionDelay: `${i * 0.1}s` }}>
                <span className="ad-proc-step-num">{step.num}</span>
                <div className="ad-proc-icon-box" style={{ background: step.bg, color: step.color }}>
                  {step.icon}
                </div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
                {i < lifecycleSteps.length - 1 && <div className="ad-proc-connector">→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 5 — TECH STACK
      ====================================================== */}
      <section className="ad-tech-section" id="ad-tech-stack">
        <div className="ad-container">
          <div className="ad-section-hdr-center ad-reveal" ref={addReveal}>
            <span className="ad-badge-lbl-purple">Technologies</span>
            <h2>Our Custom App Development Stack</h2>
            <p>We leverage modern, secure, and vendor-supported environments to build premium mobile platforms.</p>
          </div>

          <div className="ad-tech-grid">
            {techStack.map((cat, i) => (
              <div className="ad-tech-card ad-reveal" key={i} ref={addReveal} style={{ transitionDelay: `${i * 0.12}s` }}>
                <h3>{cat.category}</h3>
                <div className="ad-tech-items">
                  {cat.items.map((tech, idx) => (
                    <div key={idx} className="ad-tech-item">
                      <div className="ad-tech-item-icon">{tech.icon}</div>
                      <span>{tech.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 6 — FAQ
      ====================================================== */}
      <section className="ad-faq-section">
        <div className="ad-container">
          <div className="ad-section-hdr-center ad-reveal" ref={addReveal}>
            <span className="ad-badge-lbl">FAQ</span>
            <h2>Mobile App Development FAQ</h2>
            <p>Find answers to common questions about our native systems, publishing standards, and support scope.</p>
          </div>

          <div className="ad-faq-list">
            {faqs.map((faq, i) => (
              <div key={i} className={`ad-faq-item ${openFaq === i ? "ad-faq-open" : ""}`}>
                <button
                  className="ad-faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                >
                  <span className="ad-faq-num">Q{i + 1}</span>
                  <span className="ad-faq-q-text">{faq.q}</span>
                  <span className="ad-faq-chevron">
                    <ChevronDown size={18} style={{ transform: openFaq === i ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s" }} />
                  </span>
                </button>
                {openFaq === i && (
                  <div className="ad-faq-answer">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          SECTION 7 — CTA BANNER
      ====================================================== */}
      <section className="ad-cta-banner">
        <div className="ad-cta-banner-bg" />
        <div className="ad-container">
          <div className="ad-cta-banner-inner">
            <div className="ad-cta-banner-content">
              <h2>Ready to Design Your Mobile App?</h2>
              <p>
                Get in touch today for a free app project assessment and discovery blueprint.
                Let's discuss how we can build the ideal interface and workflow for your business.
              </p>
              <div className="ad-cta-banner-btns">
                <Link to="/contact" className="ad-cta-white-btn">
                  Get Free Consultation <ArrowRight size={16} />
                </Link>
                <a href={`tel:${companyInfo.phone}`} className="ad-cta-ghost-btn">
                  <Phone size={14} /> Call Us Now
                </a>
              </div>
            </div>
            <div className="ad-cta-contact-cards">
              <a href={`tel:${companyInfo.phone}`} className="ad-cta-contact-card">
                <div className="ad-cta-contact-icon"><Phone size={16} /></div>
                <div>
                  <div className="ad-cta-contact-label">India Office</div>
                  <div className="ad-cta-contact-val">{companyInfo.phone}</div>
                </div>
              </a>
              <a href={`mailto:${companyInfo.email}`} className="ad-cta-contact-card">
                <div className="ad-cta-contact-icon"><Send size={16} /></div>
                <div>
                  <div className="ad-cta-contact-label">Email Us</div>
                  <div className="ad-cta-contact-val">{companyInfo.email}</div>
                </div>
              </a>
              <a href={`tel:${companyInfo.phone2}`} className="ad-cta-contact-card">
                <div className="ad-cta-contact-icon"><Phone size={16} /></div>
                <div>
                  <div className="ad-cta-contact-label">Globe Office</div>
                  <div className="ad-cta-contact-val">{companyInfo.phone2}</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
